import { ChevronsUpDown, Plus } from "lucide-react";
import Image from "next/image";
import { Fragment, ReactNode, useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import AddExchangeContent from "@/app/settings/exchanges/AddExchangeForm";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { usePathname } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { addSelectedExchangeData, ExchangeKey } from "@/services/db";
import { useAppDispatch, useAppSelector } from "@/services/hooks/redux.hooks";
import useScreenSize from "@/services/hooks/screen.hooks";
import {
  resetLoadedState,
  setFocusedPortfolio,
} from "@/services/redux/dashboard.slice";
import { setSelectedExchange } from "@/services/redux/exchange-switcher.slice";
import { setSmartAllocation } from "@/services/redux/smart-allocation.slice";

import { Dialog, DialogTrigger } from "./dialog";
import { Separator } from "./separator";
import ExchangeImage from "../global/exchange-swticher/exchangeIcon";

interface Props {
  isCollapsed: boolean;
  exchanges: ExchangeKey[];
  inAsset?: boolean;
}

export const ExchangeLoadingPlaceholder = ({
  isCollapsed,
}: {
  isCollapsed: boolean;
}) => {
  if (isCollapsed) {
    return (
      <div className="group flex flex-col px-2 w-full items-center justify-center">
        <Skeleton className="w-[31px] h-[31px] rounded-full" />
      </div>
    );
  }
  return (
    <div className="flex justify-between items-center w-full border-0 border-b rounded-sm px-6 py-4 mb-4">
      <div className="animate-pulse flex items-center gap-2">
        <Skeleton className="w-[31px] h-[31px] rounded-full" />
        <>
          <div className="flex flex-col gap-1">
            <Skeleton className="w-[120px] h-3" />
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </>
      </div>
    </div>
  );
};

export function ExchangeSelect({ exchanges, isCollapsed, inAsset }: Props) {
  const { width } = useScreenSize();
  const pathname = usePathname();
  const { selectedExchange, loadingKeys } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { portfolios } = useAppSelector((state) => state.dashboard);
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const addExchange = useCallback(
    (trigger: ReactNode) => {
      return (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger className="w-full">{trigger}</DialogTrigger>
          <AddExchangeContent dialogForm />
        </Dialog>
      );
    },
    [open],
  );

  if (loadingKeys) {
    return <ExchangeLoadingPlaceholder isCollapsed={isCollapsed} />;
  }

  if (selectedExchange?.name == null) {
    if (isCollapsed) {
      return (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              {addExchange(
                <>
                  <Button asChild variant="success" size="icon">
                    <span>
                      <Plus className="h-4 w-4" />
                    </span>
                  </Button>
                  <Separator className="my-4" />
                </>,
              )}
            </TooltipTrigger>
            <TooltipContent side="right" className="flex items-center gap-4">
              <span className="ml-auto text-muted">{m.add_exchange()}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else
      return addExchange(
        <>
          <Button
            asChild
            variant="success"
            className="w-[94%] mt-4 px-4 py-3 h-auto"
          >
            <span>
              <Plus className="mr-2 h-4 w-4" /> {m.add_exchange()}
            </span>
          </Button>
          <Separator className="my-4" />
        </>,
      );
  }

  return (
    <div
      data-collapsed={isCollapsed}
      className={twMerge(
        isCollapsed
          ? ""
          : selectedExchange.name.length > 10
          ? "min-w-[175px]"
          : "min-w-fit",
        "cursor-pointer mb-4",
      )}
    >
      <DropdownMenu>
        {isCollapsed ? (
          <DropdownMenuTrigger asChild>
            <div className="group flex flex-col px-2">
              {selectedExchange.account_type === "paper" ? (
                <Image
                  key={Math.random()}
                  src="/paper.svg"
                  alt="Paper Account Icon"
                  width={30}
                  height={30}
                />
              ) : (
                <ExchangeImage
                  providerId={selectedExchange?.provider_id}
                  width={30}
                  height={30}
                />
              )}
            </div>
          </DropdownMenuTrigger>
        ) : (
          <DropdownMenuTrigger asChild>
            <Button
              className={cn(
                "bg-sidebar flex h-auto justify-between items-center border-0 border-b rounded-sm px-4 py-4",
                inAsset
                  ? "md:max-w-[300px] max-w-[95vw] border rounded-lg"
                  : "w-full",
              )}
              variant="outline"
            >
              {selectedExchange.account_type === "paper" ? (
                <Image
                  key={Math.random()}
                  src="/paper.svg"
                  alt="Paper Account Icon"
                  width={24}
                  height={24}
                />
              ) : (
                <ExchangeImage
                  providerId={selectedExchange?.provider_id}
                  width={24}
                  height={24}
                />
              )}
              <h4 className="font-bold xl:w-[70%] line-clamp-1 ms-2 inline-block truncate">
                {selectedExchange?.name}
              </h4>

              <ChevronsUpDown className="ml-auto size-4 min-w-4 min-h-4" />
            </Button>
          </DropdownMenuTrigger>
        )}

        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-sm"
          side={width <= 768 ? "bottom" : "right"}
          align="start"
          sideOffset={24}
        >
          {exchanges.map((exchange, index) => {
            const lockedKey =
              exchange.key_status_name === "CRYPTO_SUBSCRIPTION_CANCELLED";
            const isValid = exchange.key_status_name === "VALID";
            const isRejected = exchange.key_status_name === "REJECTED_KEY";
            const isReadOnly = exchange.key_status_name === "READ_ONLY";
            const text =
              exchange.account_type === "paper" || isValid
                ? m.active()
                : isRejected
                ? m.keys_expired()
                : isReadOnly
                ? m.read_only()
                : lockedKey
                ? m.premium_required()
                : m.permission_issue();
            const color =
              exchange.account_type === "paper" || isValid
                ? "text-success dark:text-green-600"
                : isReadOnly
                ? "text-warning dark:text-orange-600"
                : "text-destructive dark:text-red-600";

            let onlyValid = true;
            if (
              pathname.includes("/trade") ||
              pathname.includes("/smart-allocation") ||
              pathname === "/asset"
            ) {
              onlyValid = exchange.key_status_name === "VALID";
            } else {
              onlyValid =
                ["VALID", "READ_ONLY"].includes(
                  exchange.key_status_name as string,
                ) || exchange.provider_id == null;
            }
            const onClick = () => {
              addSelectedExchangeData(exchange);
              dispatch(setSelectedExchange(exchange));
              dispatch(
                setFocusedPortfolio(
                  portfolios.find(
                    (e) => e.id === (exchange.exchange_key_id ?? 0),
                  ) as any,
                ),
              );
              dispatch(resetLoadedState());
              dispatch(setSmartAllocation(undefined));
            };
            return (
              <Fragment key={exchange.exchange_key_id ?? 0}>
                <DropdownMenuItem
                  key={index}
                  className="cursor-pointer py-2 "
                  onClick={onClick}
                  disabled={!onlyValid}
                >
                  <div className="flex items-center gap-2">
                    {exchange.account_type === "paper" ? (
                      <div className="w-[24px] h-[24px] flex items-center justify-center rounded-full bg-slate-500">
                        <Image
                          key={Math.random()}
                          src="/paper.svg"
                          alt="Paper Account Icon"
                          width={14}
                          height={14}
                        />
                      </div>
                    ) : (
                      <ExchangeImage
                        providerId={exchange.provider_id}
                        width={24}
                        height={24}
                      />
                    )}
                    <div className="flex flex-col gap-1 w-[150px]">
                      <h4 className="font-bold text-sm leading-none truncate inline-block line-clamp-1">
                        {exchange.name}
                      </h4>
                      {exchange.exchange_key_id != null && (
                        <span
                          className={`${color} text-[9px] leading-none font-bold tracking-wide`}
                        >
                          {text}
                        </span>
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </Fragment>
            );
          })}
          {addExchange(
            <Button
              asChild
              className="h-[44px] font-semibold cursor-pointer w-full"
              variant="ghost"
            >
              <span>
                <Plus className="mr-2 h-4 w-4" /> {m.add_exchange()}
              </span>
            </Button>,
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
